import type { RecipeTimes, TimeBucket } from './types.ts';
import { TOTAL_TIME_BUCKETS } from './types.ts';

/**
 * Summe der Zeit, in der man tatsächlich in der Küche steht.
 * Rüsten und aktive Arbeit gehören dazu, Warten und Kühlen nicht.
 */
export function activeMinutes(times: RecipeTimes): number {
  return times.preparationMinutes + times.activeMinutes;
}

/**
 * Gesamtzeit vom Start bis zum fertigen Gericht.
 * Vorbereitung, Kochen, Backen und Warten laufen zum Teil parallel,
 * deshalb wird hier bewusst die Summe gebildet: Sie ist die ehrliche
 * Angabe für die Planung.
 */
export function totalMinutes(times: RecipeTimes): number {
  return (
    times.preparationMinutes +
    times.activeMinutes +
    times.cookingMinutes +
    times.bakingMinutes +
    times.restingMinutes +
    times.coolingMinutes
  );
}

/** Ergänzt fehlende Zeitangaben, damit gerechnet werden kann. */
export function normalizeTimes(times: Partial<RecipeTimes>): RecipeTimes {
  return {
    preparationMinutes: times.preparationMinutes ?? 0,
    activeMinutes: times.activeMinutes ?? 0,
    cookingMinutes: times.cookingMinutes ?? 0,
    bakingMinutes: times.bakingMinutes ?? 0,
    restingMinutes: times.restingMinutes ?? 0,
    coolingMinutes: times.coolingMinutes ?? 0,
  };
}

/**
 * Formatiert Minuten auf Deutsch, zum Beispiel "1 Std. 25 Min." oder "45 Min.".
 * Zeiten unter einer Minute werden als "unter 1 Min." ausgegeben.
 */
export function formatMinutes(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes < 0) {
    return '–';
  }
  if (minutes === 0) {
    return '0 Min.';
  }
  if (minutes < 1) {
    return 'unter 1 Min.';
  }
  const rounded = Math.round(minutes);
  const hours = Math.floor(rounded / 60);
  const rest = rounded % 60;
  if (hours === 0) {
    return `${rest} Min.`;
  }
  if (rest === 0) {
    return `${hours} Std.`;
  }
  return `${hours} Std. ${rest} Min.`;
}

/**
 * Kurzform für enge Stellen wie Rezeptkarten, zum Beispiel "1:25 Std."
 * oder "45 Min.".
 */
export function formatMinutesShort(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes <= 0) {
    return '–';
  }
  const rounded = Math.round(minutes);
  if (rounded < 60) {
    return `${rounded} Min.`;
  }
  const hours = Math.floor(rounded / 60);
  const rest = rounded % 60;
  return rest === 0 ? `${hours} Std.` : `${hours}:${String(rest).padStart(2, '0')} Std.`;
}

/** Findet die passende Zeitstufe zu einer Gesamtzeit. */
export function bucketForTotalMinutes(minutes: number): TimeBucket | undefined {
  return TOTAL_TIME_BUCKETS.find((bucket) => minutes <= bucket.maxMinutes);
}
